import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import { useAlert } from '../../context/AlertContext';
import { roleService } from '../../services/roleService';
import { supabase } from '../../supabase/config';
import { Users, ShieldCheck, UserPlus, RefreshCw } from 'lucide-react';

// Sub-components
import UserTable from './users/UserTable';
import RoleGrid from './users/RoleGrid';
import RoleModal from './users/RoleModal';
import InviteUserModal from './users/InviteUserModal';

const UsersAndRoles = () => {
    const { t } = useTranslation();
    const { effectiveTenantId, user, userData, hasPermission, permissionsLoading } = useAuth();
    const { showAlert, showConfirm } = useAlert();
    const canManageRoles = userData?.role === 'owner' || userData?.isSuperAdmin || hasPermission('roles', 'manage');

    const [activeTab, setActiveTab] = useState('users');
    const [users, setUsers] = useState([]);
    const [roles, setRoles] = useState([]);
    const [tenants, setTenants] = useState({});
    const [loading, setLoading] = useState(true);
    const [modalOpen, setModalOpen] = useState(null); // 'user' or 'role'
    const [editingRole, setEditingRole] = useState(null);

    const initialRole = {
        name: '',
        permissions: {
            customers: { view: true, create: false, edit: false, delete: false },
            discounts: { view: true, create: false, edit: false, delete: false },
            rewards: { view: true, create: false, edit: false, delete: false },
            menu: { view: true, create: false, edit: false, delete: false },
            notifications: { view: true, create: false, edit: false, delete: false },
            activities: { view: true },
            settings: { view: false, edit: false },
            roles: { view: false, manage: false }
        }
    };
    const [roleData, setRoleData] = useState(initialRole);

    useEffect(() => {
        if (!effectiveTenantId && userData?.isSuperAdmin) {
            supabase.from('tenants').select('id, name').then(({ data }) => {
                const map = {};
                (data || []).forEach(d => map[d.id] = d.name);
                setTenants(map);
            });
        }
    }, [effectiveTenantId, userData?.isSuperAdmin]);

    useEffect(() => {
        let unsubs = [];
        setLoading(true);
        unsubs.push(roleService.subscribeToUsers(effectiveTenantId, userData?.isSuperAdmin, setUsers));
        if (effectiveTenantId) {
            unsubs.push(roleService.subscribeToRoles(effectiveTenantId, (data) => {
                setRoles(data);
                setLoading(false);
            }));
        } else { setLoading(false); }
        return () => unsubs.forEach(u => u && u());
    }, [effectiveTenantId, userData?.isSuperAdmin]);

    const handleSaveRole = async (e) => {
        e.preventDefault();
        try {
            await roleService.saveRole(effectiveTenantId, roleData, editingRole?.id, user.uid, userData.email);
            setModalOpen(null);
        } catch (err) { showAlert("Hata oluştu.", "error"); }
    };

    const handleDeleteRole = async (role) => {
        if (await showConfirm(`${role.name} rolünü silmek istediğinize emin misiniz?`, { title: 'Rolü Sil', confirmText: 'Sil', type: 'warning' })) {
            await roleService.deleteRole(effectiveTenantId, role.id, role.name, user.uid, userData.email);
        }
    };

    if (permissionsLoading || loading) return <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '60vh' }}><RefreshCw className="animate-spin" /></div>;

    return (
        <div className="fade-in">
            <div style={{ marginBottom: '2rem' }}>
                <h1 style={{ fontWeight: 800 }}>{t('roles.title')}</h1>
                <p style={{ color: 'var(--text-muted)' }}>{t('roles.subtitle')}</p>
            </div>

            <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '2.5rem', background: 'rgba(var(--primary-rgb), 0.05)', padding: '0.5rem', borderRadius: '16px', width: 'fit-content', maxWidth: '100%', overflowX: 'auto' }} className="hide-scrollbar">
                <button onClick={() => setActiveTab('users')} style={{ padding: '10px 24px', borderRadius: '12px', border: 'none', background: activeTab === 'users' ? 'var(--primary)' : 'transparent', color: activeTab === 'users' ? 'white' : 'var(--text-main)', fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: '8px' }}><Users size={18} /> {t('roles.usersTab')}</button>
                <button onClick={() => setActiveTab('roles')} style={{ padding: '10px 24px', borderRadius: '12px', border: 'none', background: activeTab === 'roles' ? 'var(--primary)' : 'transparent', color: activeTab === 'roles' ? 'white' : 'var(--text-main)', fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: '8px' }}><ShieldCheck size={18} /> {t('roles.rolesTab')}</button>
            </div>

            {activeTab === 'users' ? (
                <section>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem', flexWrap: 'wrap', gap: '1rem' }}>
                        <h2 style={{ fontSize: '1.25rem', fontWeight: 700 }}>{t('roles.usersTab')}</h2>
                        {canManageRoles && <button onClick={() => setModalOpen('user')} className="btn-primary" style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 20px', borderRadius: '12px', fontWeight: 700 }}><UserPlus size={18} /> {t('roles.addUser')}</button>}
                    </div>
                    <UserTable users={users} tenants={tenants} isSuperAdmin={userData?.isSuperAdmin} effectiveTenantId={effectiveTenantId} canManageRoles={canManageRoles} t={t} />
                </section>
            ) : (
                <section>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem', flexWrap: 'wrap', gap: '1rem' }}>
                        <h2 style={{ fontSize: '1.25rem', fontWeight: 700 }}>{t('roles.rolesTab')}</h2>
                        {canManageRoles && <button onClick={() => { setEditingRole(null); setRoleData(initialRole); setModalOpen('role'); }} className="btn-primary" style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 20px', borderRadius: '12px', fontWeight: 700 }}><ShieldCheck size={18} /> {t('roles.addRole')}</button>}
                    </div>
                    <RoleGrid roles={roles} canManageRoles={canManageRoles} onEdit={(r) => { setEditingRole(r); setRoleData(r); setModalOpen('role'); }} onDelete={handleDeleteRole} t={t} />
                </section>
            )}
            <style>{`
                .hide-scrollbar::-webkit-scrollbar { display: none; }
                @media (max-width: 600px) {
                    .fade-in h1 { fontSize: 1.5rem !important; }
                    .fade-in p { fontSize: 0.85rem !important; }
                }
            `}</style>

            <RoleModal isOpen={modalOpen === 'role'} onClose={() => setModalOpen(null)} onSave={handleSaveRole} role={roleData} setRole={setRoleData} isEditing={!!editingRole} t={t} />
            <InviteUserModal isOpen={modalOpen === 'user'} onClose={() => setModalOpen(null)} onInvite={(u) => roleService.createInvite(effectiveTenantId, u, user.uid, userData.email)} roles={roles} t={t} />
        </div>
    );
};

export default UsersAndRoles;
